import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import {
  AlertProps as MuiAlertProps,
  Box,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Typography,
  useMediaQuery,
} from "@mui/material";
import theme from "../../elements/Theme";
import emailjs from "@emailjs/browser";
import OrangeHighlightButton from "../../elements/OrangeHighlightButton";
import AlertComponent from "../../elements/AlertComponent";
import LoadingComponent from "../../elements/Loading";

interface EnrollFormFrameProps {
  formRef?: React.RefObject<HTMLFormElement | null>;
}

const EnrollFormFrame: React.FC<EnrollFormFrameProps> = ({ formRef }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const reCaptchakey = "6LfHi2srAAAAAF3fQOjlrQDOXI2RtBHxQ2dUvr9L";
  // translations
  const { t, i18n } = useTranslation();
  let currentLanguage = i18n.language;
  // theme
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // data
  const [formValues, setFormValues] = useState({
    childName: "",
    childGender: "",
    childBirthday: "",
    parentName: "",
    phone: "",
    email: "",
    comments: "",
    educationLevel: "",
  });
  // utils
  const [isLoading, setIsLoading] = useState(false);
  const [isAlert, setIsAlert] = useState({
    open: false,
    severity: "" as MuiAlertProps["severity"],
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // reCAPTCHA
    if (!recaptchaToken) {
      setIsAlert({
        open: true,
        severity: "error",
        message: t("EnrollFormFrame.Please verify that you're not a robot."),
      });
      return;
    }

    setIsLoading(true);
    const form = event.currentTarget;
    try {
      // emailjs
      emailjs.init("0VR9T4-49oj__IUhF");
      await emailjs.sendForm(
        "service_xg8pn69",
        "template_l1gjrmc",
        form,
        "0VR9T4-49oj__IUhF"
      );

      // push dataLayer GTM
      try {
        if (typeof window !== "undefined") {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: "form_submit" });
        }
      } catch (err) {
        console.warn("GTM dataLayer push failed:", err);
      }

      // If the email is sent successfully, show a message and clear form values
      setIsAlert({
        open: true,
        severity: "success",
        message: t("EnrollFormFrame.Thank you for your submission!"),
      });
      setIsSubmitted(true);
      setFormValues({
        childName: "",
        childGender: "",
        childBirthday: "",
        parentName: "",
        phone: "",
        email: "",
        comments: "",
        educationLevel: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setIsAlert({
        open: true,
        severity: "error",
        message: t(
          "EnrollFormFrame.Failed to send email. Please try again later."
        ),
      });
    } finally {
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        px={isMobile ? "10px" : "73px"}
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.primary.main,
          alignItems: "center",
          mt: isMobile ? "10vh" : "16vh",
          mb: isMobile ? "10vh" : "20vh",
        }}
      >
        <Box ref={formRef}>
          <Typography
            variant={"h3"}
            fontWeight={600}
            sx={{ color: theme.palette.text.secondary }}
            textAlign="center"
            mb="5vh"
          >
            {t("EnrollFormFrame.title")}
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 4,
            borderRadius: "25px",
            boxShadow: "4px 4px 6px 0px rgba(0, 0, 0, 0.5)",
            width: { sm: "80%", xs: "100%" },
            "& .MuiTextField-root": {
              margin: "8px 0",
              "&.Mui-focused": {
                color: "black", // or any color you want for the focused state
              },
            },
            "& .MuiButton-root": { marginTop: "16px" },
            "& .MuiFormControlLabel-root": {
              display: "block",
              margin: "8px 0",
            },
            "& .MuiRadio-root": {
              color: theme.palette.secondary.main,
              "&.Mui-checked": {
                color: theme.palette.secondary.main,
              },
              "&.Mui-focused": {
                color: "black", // or any color you want for the focused state
              },
            },
            "& .MuiTypography-root": {
              color: theme.palette.text.primary,
            },
            "& .MuiInputBase-input": {
              color: theme.palette.text.primary,
            },
            "& .MuiFormLabel-root": {
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.secondary.main,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: theme.palette.secondary.main,
              },
          }}
        >
          <TextField
            fullWidth
            name="childName"
            label={t("EnrollFormFrame.Your child's full name")}
            variant="outlined"
            value={formValues.childName}
            onChange={handleInputChange}
          />
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t("EnrollFormFrame.Your child's gender")}
            </FormLabel>
            <RadioGroup row name="childGender" onChange={handleRadioChange}>
              <FormControlLabel
                value="male"
                control={<Radio />}
                label={t("EnrollFormFrame.Male")}
              />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label={t("EnrollFormFrame.Female")}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            name="childBirthday"
            label={t("EnrollFormFrame.Your child's date of birth")}
            variant="outlined"
            value={formValues.childBirthday}
            onChange={handleInputChange}
          />
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t("EnrollFormFrame.Education Level")}
            </FormLabel>
            <RadioGroup name="educationLevel" onChange={handleRadioChange}>
              <FormControlLabel
                value="Nursery: 1-3 years"
                control={<Radio />}
                label={t("EnrollFormFrame.1-3 years")}
              />
              <FormControlLabel
                value="Children: 3-4 years"
                control={<Radio />}
                label={t("EnrollFormFrame.3-4 years")}
              />
              <FormControlLabel
                value="Children: 4-5 years"
                control={<Radio />}
                label={t("EnrollFormFrame.4-5 years")}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            name="parentName"
            label={t("EnrollFormFrame.Your full name")}
            variant="outlined"
            required
            value={formValues.parentName}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            name="phone"
            label={t("EnrollFormFrame.Your phone")}
            variant="outlined"
            value={formValues.phone}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            name="email"
            label={t("EnrollFormFrame.Your email")}
            variant="outlined"
            required
            value={formValues.email}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            name="comments"
            label={t("EnrollFormFrame.Special requirements or comments")}
            variant="outlined"
            value={formValues.comments}
            onChange={handleInputChange}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <br />
            {reCaptchakey && (
              <ReCAPTCHA
                key={currentLanguage}
                ref={recaptchaRef}
                sitekey={reCaptchakey}
                hl={currentLanguage}
                onChange={(token) => setRecaptchaToken(token)}
              />
            )}
            <br />
            {!isSubmitted ? (
              <OrangeHighlightButton type="submit">
                <Typography variant="h5" sx={{ color: "white !important" }}>
                  {t("EnrollFormFrame.SUBMIT")}
                </Typography>
              </OrangeHighlightButton>
            ) : (
              <Typography variant={"h3"}>
                {t("EnrollFormFrame.Thank you for your submission!")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {isAlert.open && (
        <AlertComponent isAlert={isAlert} setIsAlert={setIsAlert} />
      )}

      <LoadingComponent loading={isLoading} />
    </>
  );
};

export default EnrollFormFrame;
